import { Button } from "@heroui/react";
import { EmptyState, ErrorState, SkeletonTable } from "@branding/ui";
import { mutate as globalMutate } from "swr";
import useSWR from "swr";
import { NoIndex } from "../components/NoIndex";
import {
  fetchCart,
  removeCartLine,
  updateCartLine,
} from "../lib/api";
import { formatIdr } from "../lib/format";
import { productPath } from "../lib/json-ld";
import type { Cart } from "../lib/types";
import { ApiClientError } from "../lib/v2";

export function CartPage() {
  const { data, error, isLoading, mutate } = useSWR<Cart>("cart", fetchCart);
  const lines = data?.lines ?? [];
  const merchants = new Set(lines.map((line) => line.merchant_id));
  const subtotal = lines.reduce((sum, line) => sum + line.price * line.qty, 0);

  async function changeQty(skuId: string, qty: number) {
    if (qty < 1) {
      await removeCartLine(skuId);
    } else {
      await updateCartLine(skuId, qty);
    }
    await mutate();
    await globalMutate("cart");
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6">
      <NoIndex />
      <h1 className="text-3xl font-semibold">Keranjang</h1>
      {error ? (
        <div className="mt-8">
          <ErrorState
            title="Keranjang tidak bisa dibuka"
            description={
              error instanceof ApiClientError ? error.description ?? undefined : undefined
            }
            onRetry={() => {
              void mutate();
            }}
          />
        </div>
      ) : isLoading && !data ? (
        <div className="mt-8">
          <SkeletonTable />
        </div>
      ) : lines.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="Keranjang masih kosong"
            description="Pilih produk dari etalase, lalu pilih varian SKU."
            action={
              <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
                Jelajahi pasar
              </Button>
            }
          />
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-6">
          {merchants.size > 1 ? (
            <p className="text-sm text-warning" role="status">
              Keranjang berisi lebih dari satu toko. Checkout hanya untuk satu
              toko per pesanan.
            </p>
          ) : null}
          <ul className="flex flex-col gap-4">
            {lines.map((line) => (
              <li
                key={line.sku_id}
                className="flex flex-col gap-2 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <a href={productPath(line.product_slug)} className="font-medium">
                    {line.product_name}
                  </a>
                  <p className="text-sm text-muted">{line.sku_code}</p>
                  <p className="pasar-price mt-1 text-sm">
                    {formatIdr(line.price)} · stok {line.available}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={`Kurangi ${line.product_name}`}
                    onPress={() => {
                      void changeQty(line.sku_id, line.qty - 1);
                    }}
                  >
                    −
                  </Button>
                  <span className="pasar-price min-w-8 text-center">{line.qty}</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={`Tambah ${line.product_name}`}
                    isDisabled={line.qty >= line.available}
                    onPress={() => {
                      void changeQty(line.sku_id, line.qty + 1);
                    }}
                  >
                    +
                  </Button>
                  <Button
                    size="sm"
                    variant="tertiary"
                    onPress={() => {
                      void changeQty(line.sku_id, 0);
                    }}
                  >
                    Hapus
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted">Subtotal</p>
            <p className="pasar-price text-xl font-semibold">{formatIdr(subtotal)}</p>
          </div>
          <Button variant="primary" onPress={() => window.location.assign("/pasar/checkout")}>
            Lanjut ke checkout
          </Button>
        </div>
      )}
    </main>
  );
}
